export const CURSOR_SURFACE_ATTRIBUTE = 'data-cursor-surface';
export const CURSOR_SURFACE = {
    light: 'light',
    dark: 'dark',
} as const;

type CursorSurface = typeof CURSOR_SURFACE[keyof typeof CURSOR_SURFACE];

interface IRgbaColor {
    r: number,
    g: number,
    b: number,
    a: number,
}

const LIGHT_CURSOR_CHANNEL_THRESHOLD = 37.5;
const MIN_VISIBLE_BACKGROUND_ALPHA = 0.5;

const parseBackgroundColor = (backgroundColor: string): IRgbaColor | null => {
    const match = backgroundColor.match(/rgba?\(([^)]+)\)/i);
    if (!match) return null;

    const parts = match[1].split(',').map((part) => parseFloat(part.trim()));
    if (parts.length < 3 || parts.slice(0, 3).some((part) => Number.isNaN(part))) {
        return null;
    }

    return {
        r: parts[0],
        g: parts[1],
        b: parts[2],
        a: parts.length < 4 || Number.isNaN(parts[3]) ? 1 : parts[3],
    };
};

const getExplicitCursorSurface = (element: HTMLElement): CursorSurface | null => {
    const surface = element.getAttribute(CURSOR_SURFACE_ATTRIBUTE);
    if (surface === CURSOR_SURFACE.light || surface === CURSOR_SURFACE.dark) {
        return surface;
    }

    return null;
};

const isLightSurface = (rgba: IRgbaColor): boolean => (
    rgba.r > LIGHT_CURSOR_CHANNEL_THRESHOLD
    && rgba.g > LIGHT_CURSOR_CHANNEL_THRESHOLD
    && rgba.b > LIGHT_CURSOR_CHANNEL_THRESHOLD
);

const shouldUseDarkCursor = (element: HTMLElement | null): boolean => {
    let currentElement = element;

    while (currentElement) {
        const explicitSurface = getExplicitCursorSurface(currentElement);
        if (explicitSurface === CURSOR_SURFACE.light) return true;
        if (explicitSurface === CURSOR_SURFACE.dark) return false;

        const backgroundColor = window
            .getComputedStyle(currentElement, null)
            .getPropertyValue('background-color');
        const rgba = parseBackgroundColor(backgroundColor);

        if (rgba && rgba.a >= MIN_VISIBLE_BACKGROUND_ALPHA) {
            return isLightSurface(rgba);
        }

        currentElement = currentElement.parentElement;
    }

    return false;
};

export default shouldUseDarkCursor;
